export interface LessonRowStyle {
  row: string
  text: string
}

/**
 * True wenn die Lektion vollständig storniert ist. Zentral geteilt zwischen
 * IncomingRequestCard und PriorityRequestCard — komplett stornierte Lektionen
 * werden in beiden Tabellen ausgeblendet (nicht als Zeile gerendert).
 */
export function isCancelledLesson(l: { status?: string | null; assignment_state?: string | null }): boolean {
  const state = l.assignment_state ?? l.status
  return l.status === 'cancelled' || state === 'cancelled'
}

/**
 * Berechnet die Zeilenfarbe für eine Lektion basierend auf dem Status.
 *
 * Farb-Semantik (perspective = 'viewer', Standard — Anfragen AN mich):
 * - Grün: Lektion von mir übernommen
 * - Grau: Lektion von anderem Lehrer übernommen
 *
 * Farb-Semantik (perspective = 'owner' — Anfragen VON mir, debug-fix-v2):
 * - Grün: Lektion definitiv vergeben (an IRGENDWEN) — für den Anfragenden
 *   zählt, dass die Lektion gedeckt ist, nicht wer sie übernahm. Entspricht
 *   den Farben der Kaskaden-Hover-Tabelle.
 *
 * Gemeinsam:
 * - Rot: Abgelehnt
 * - Blau: Wartepool
 * - Grau: Storniert
 * - Orange: Offen
 *
 * @param assignmentState - Der assignment_state der Lektion
 * @param assignedTeacherId - Die ID des Lehrers, der die Lektion übernommen hat
 * @param myTeacherId - Die ID des aktuellen Lehrers (undefined für Admin)
 * @param perspective - 'viewer' (Default: Anfragen an mich) | 'owner' (Anfragen von mir)
 */
export function getLessonRowStyle(
  assignmentState: string | null | undefined,
  assignedTeacherId: string | null | undefined,
  myTeacherId: string | null | undefined,
  perspective: 'viewer' | 'owner' = 'viewer',
): LessonRowStyle {
  const isMine = assignedTeacherId && myTeacherId && assignedTeacherId === myTeacherId
  const isOther = assignedTeacherId && (!myTeacherId || assignedTeacherId !== myTeacherId)

  // Owner-Perspektive (Anfragen von mir): 'accepted'/'confirmed' → grün,
  // unabhängig davon, welcher Lehrer übernommen hat.
  if (perspective === 'owner' && (assignmentState === 'confirmed' || assignmentState === 'accepted')) {
    return {
      row: 'bg-green-100 dark:bg-green-900/30',
      text: 'text-green-800 dark:text-green-300',
    }
  }

  // Grün: Lektion von mir übernommen
  if ((assignmentState === 'confirmed' || assignmentState === 'accepted') && isMine) {
    return {
      row: 'bg-green-100 dark:bg-green-900/30',
      text: 'text-green-800 dark:text-green-300',
    }
  }
  
  // Grau: Lektion von anderem Lehrer uebernommen (auch accepted_not_selected)
  if ((assignmentState === 'confirmed' || assignmentState === 'accepted' || assignmentState === 'accepted_not_selected') && isOther) {
    return {
      row: 'bg-gray-100 dark:bg-gray-800/30',
      text: 'text-gray-600 dark:text-gray-400',
    }
  }
  
  // Rot: Abgelehnt
  if (assignmentState === 'declined') {
    return {
      row: 'bg-red-100 dark:bg-red-900/30',
      text: 'text-red-800 dark:text-red-300',
    }
  }
  
  // Blau: Wartepool
  if (assignmentState === 'waiting') {
    return {
      row: 'bg-blue-100 dark:bg-blue-900/30',
      text: 'text-blue-800 dark:text-blue-300',
    }
  }

  // Grau: Storniert
  if (assignmentState === 'cancelled') {
    return {
      row: 'bg-gray-200 dark:bg-gray-700/40',
      text: 'text-gray-500 dark:text-gray-400',
    }
  }

  // Orange: Offen (alle anderen States)
  return {
    row: 'bg-orange-100 dark:bg-orange-900/30',
    text: 'text-orange-800 dark:text-orange-300',
  }
}
